import { createFieldMarker } from 'app/components/MarkerIcon/MarkerIcon';
import { IFieldFeature } from 'app/containers/Map/slice/types';
import { getPolygonCenterPosition } from 'app/containers/Task/slice/helpers';
import { RouteData } from 'app/containers/Task/slice/types';
import React, { FC, Fragment } from 'react';
import { Marker, Polygon, Polyline, GeoJSON } from 'react-leaflet';
import { getAreaWithUnits } from 'utils/helpers';
import { getRouteOrRouteFieldColor } from './RouteMapItems.helpers';
import buffer from '@turf/buffer';
import { reorder } from 'app/containers/Map/slice/helpers';

type CommonProps = {
  routes: RouteData[];
  onFieldClick: ({
    fieldSeasonId,
    routeId,
  }: {
    fieldSeasonId?: number | undefined;
    routeId?: number | undefined;
  }) => void;
  checkedRouteIds?: number[];
  routeFields?: {
    field: IFieldFeature;
    routesOnThisField: RouteData[];
  }[];
};

type HighlightRouteProps = {
  isHighlightRouteEnabled: true;
  highlightedRoutesGpsVehicleId?: number | null;
};

type Props =
  | (CommonProps & {
      isHighlightRouteEnabled?: undefined;
      highlightedRoutesGpsVehicleId?: undefined;
    })
  | (CommonProps & HighlightRouteProps);

const POLYLINE_PATH_OPTIONS = {
  opacity: 1,
  fillOpacity: 1,
  weight: 2,
  dashArray: '10 15',
  dashOffset: '10',
};

const RouteMapItems: FC<Props> = ({
  routes,
  onFieldClick,
  checkedRouteIds,
  routeFields,
  isHighlightRouteEnabled,
  highlightedRoutesGpsVehicleId,
}) => {
  const routesWithImplementWidth = routes.filter(route => route.implementWidth);

  return (
    <>
      {routes.map(route => {
        const color = getRouteOrRouteFieldColor({
          checkedRouteIds,
          gpsVehicleColor: route.gpsVehicle.color,
          routeIds: [route.id],
        });

        const isHighlighted =
          !isHighlightRouteEnabled ||
          route.gpsVehicle.id === highlightedRoutesGpsVehicleId;

        const opacity = isHighlighted ? 1 : 0.5;

        return (
          <Polyline
            key={route.id}
            pathOptions={{
              ...POLYLINE_PATH_OPTIONS,
              opacity: opacity,
              fillOpacity: opacity,
              fillColor: color,
              color: color,
            }}
            positions={route.geometry.coordinates}
            eventHandlers={{
              click() {
                onFieldClick({
                  fieldSeasonId: route.fieldSeasonId,
                  routeId: route.id,
                });
              },
            }}
          />
        );
      })}

      {/* Implement buffer */}
      {routesWithImplementWidth.map(route => {
        // TODO: geometry types
        const bufferGeometry: any = {
          ...route.geometry,
          coordinates: reorder(route.geometry.coordinates).flat(2),
        };

        const turfbuffer = buffer(
          bufferGeometry,

          route.implementWidth as number,
          { units: 'meters' },
        );

        const color = getRouteOrRouteFieldColor({
          checkedRouteIds,
          gpsVehicleColor: route.gpsVehicle.color,
          routeIds: [route.id],
        });

        return (
          <GeoJSON
            key={route.id}
            data={turfbuffer}
            pathOptions={{
              opacity: 0.5,
              fillOpacity: 0.5,
              fillColor: color,
              color: color,
            }}
          />
        );
      })}

      {routeFields?.map(({ field, routesOnThisField }) => {
        const isActive = Boolean(
          routesOnThisField.find(route => checkedRouteIds?.includes(route.id)),
        );

        const polygonFillOpacity = isActive ? 0.1 : 0.8;

        const lastRouteOnThisField = routesOnThisField[0];

        const eventHandlers = {
          click() {
            onFieldClick({
              fieldSeasonId: field.properties.id,
              routeId: lastRouteOnThisField.id,
            });
          },
        };

        const color = getRouteOrRouteFieldColor({
          checkedRouteIds,
          gpsVehicleColor: lastRouteOnThisField.gpsVehicle.color,
          routeIds: routesOnThisField.map(route => route.id),
        });

        const markerPosition = getPolygonCenterPosition(
          field.geometry.coordinates,
        );

        const area = getAreaWithUnits(field.properties.area * 10000);

        const markerIcon = createFieldMarker({
          selected: false,
          name: field.properties.field_name,
          culture: field.properties.culture_name,
          colorClass: color,
          area,
        });

        return (
          <Fragment key={field.id}>
            <Polygon
              pathOptions={{
                fillColor: color,
                color: color,
                opacity: 0.9,
                fillOpacity: polygonFillOpacity,
              }}
              positions={field.geometry.coordinates}
              eventHandlers={eventHandlers}
            />
            {markerPosition && !isActive && (
              <Marker
                eventHandlers={eventHandlers}
                position={markerPosition}
                icon={markerIcon}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default RouteMapItems;
